import { Link } from "react-router-dom";
import { ArrowRight, Building2, CreditCard, ShoppingCart, Wallet, Users, ShieldCheck, Code2 } from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import MarketingPageShell from "../components/MarketingPageShell";
import usePageMeta from "../hooks/usePageMeta";

const SECTORS = [
  { icon: Building2, t: "Banks & MFIs", d: "Embed cyber cover into your merchant app. Branded portal, partner-share commission." },
  { icon: CreditCard, t: "PSPs", d: "Offer cover-at-checkout on every M-Pesa till or POS device you settle for." },
  { icon: Wallet, t: "SACCOs", d: "Protect member merchants from cyber loss. Bulk-onboarding tools, revenue share." },
  { icon: ShoppingCart, t: "POS networks", d: "Cover every till on your network. White-label dashboards for franchise operators." },
  { icon: ShieldCheck, t: "Insurers & brokers", d: "Distribute Fortiqa's cyber-cover product alongside your existing book." },
  { icon: Users, t: "Aggregators", d: "Saccos, market federations, agent networks — bulk onboarding + group billing." },
  { icon: Code2, t: "E-commerce platforms", d: "Add cover-at-checkout for every seller on your marketplace. JS widget + REST API." },
];

export default function Partners() {
  usePageMeta({
    title: "Partner with Fortiqa",
    description:
      "Distribute cyber cover to every merchant in your network. Branded portals, revenue share, and a public API.",
  });
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="partners-page">
      <MarketingPageShell
        eyebrow="Partner program"
        title="Distribute cyber cover to every merchant in your network."
        intro="We provide the policy, the engine, the claims team, and the regulator-ready paperwork. You provide the merchants — and earn a recurring share on every active cover."
        testid="partners"
      >
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SECTORS.map(({ icon: Icon, t, d }) => (
            <div
              key={t}
              className="rounded-2xl bg-white border border-[#114232]/10 p-6 hover:border-[#114232]/30 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-[#114232]/8 grid place-items-center mb-4">
                <Icon className="w-5 h-5 text-[#114232]" strokeWidth={1.7} />
              </div>
              <div className="font-heading text-base font-bold text-[#114232] mb-1.5">{t}</div>
              <p className="text-sm text-[#5c6360] leading-relaxed">{d}</p>
            </div>
          ))}
        </section>

        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
          <div className="bg-[#114232] rounded-3xl p-8 sm:p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">
                Let's talk
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold leading-tight">
                Partner with Fortiqa.
              </h3>
              <p className="text-[#f4f2eb]/80 mt-2 max-w-md text-sm sm:text-base">
                Pitch deck, revenue model, API docs — we'll send everything within 24 hours.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:partners@fortiqa.co.ke?subject=Partner%20with%20Fortiqa"
                data-testid="partners-cta-email"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-all shadow-md hover:shadow-lg"
              >
                Partner with Fortiqa <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/docs/embed"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/15 text-[#f4f2eb] font-semibold transition-all border border-white/20"
              >
                Read API docs
              </Link>
            </div>
          </div>
        </section>
      </MarketingPageShell>
      <MarketingFooter />
    </div>
  );
}
